import type { SearchArea } from '@/lib/areaSearch';
import { PLACE_RADIUS_M } from '@/lib/serpRead/miejsce';
import type { RecentPlace } from '@/types/savedSearch';

/**
 * „BLISKO DO…” — reguły wiersza w panelu filtrów (plan §1.67, §1.70, E4.3). Czyste funkcje:
 * próg i opóźnienie podpowiedzi, wybór miejsca, lista „Ostatnio wybrane”.
 */

/** Podpowiedzi startują od 3 znaków (§1.67). */
export const MIN_ZNAKOW_MIEJSCA = 3;
/** Opóźnienie po ostatnim naciśnięciu klawisza (§1.67, ~300 ms). */
export const DEBOUNCE_MIEJSCA_MS = 300;
/** „Ostatnio wybrane”: najwyżej 3 miejsca (§1.70; ten sam limit pilnuje `save_my_filters`). */
export const MAKS_OSTATNICH = 3;

export type RodzajMiejsca = 'school' | 'work' | 'other';

/** Wiersz podpowiedzi, jak go zwraca funkcja `place-autocomplete`. */
export interface PodpowiedzMiejsca {
  label: string;
  address: string;
  lat: number;
  lon: number;
  kind: RodzajMiejsca;
}

export type StatusPodpowiedzi = 'ok' | 'none' | 'limited' | 'unavailable';

/** Tekst po przycięciu — to on decyduje o progu i jedzie do funkcji. */
export function tekstZapytania(q: string): string {
  return q.trim().replace(/\s+/g, ' ');
}

/** Czy wysłać zapytanie: od 3 znaków po przycięciu. */
export function czySzukac(q: string): boolean {
  return tekstZapytania(q).length >= MIN_ZNAKOW_MIEJSCA;
}

/** „Ostatnio wybrane” widać tylko przy pustym (krótszym niż próg) polu i bez wybranego miejsca. */
export function czyPokazacOstatnie(q: string, ostatnie: readonly RecentPlace[], maMiejsce: boolean): boolean {
  return !maMiejsce && ostatnie.length > 0 && !czySzukac(q);
}

/** Obszar wyszukiwania z wybranego miejsca — promień zawsze 800 m (§1.18). */
export function obszarZMiejsca(p: Pick<RecentPlace, 'label' | 'lat' | 'lon'>): Extract<SearchArea, { kind: 'place' }> {
  return { kind: 'place', label: p.label, lat: p.lat, lon: p.lon, radiusM: PLACE_RADIUS_M };
}

const toSamo = (a: Pick<RecentPlace, 'label' | 'lat' | 'lon'>, b: Pick<RecentPlace, 'label' | 'lat' | 'lon'>) =>
  a.label === b.label && Math.abs(a.lat - b.lat) < 1e-6 && Math.abs(a.lon - b.lon) < 1e-6;

/** Nowe miejsce na początek, bez duplikatu, najwyżej 3 (makieta: `rememberPlace`). */
export function zapamietajMiejsce(lista: readonly RecentPlace[], p: RecentPlace): RecentPlace[] {
  const wpis: RecentPlace = { label: p.label, lat: p.lat, lon: p.lon };
  if (p.address) wpis.address = p.address;
  if (p.kind) wpis.kind = p.kind;
  return [wpis, ...lista.filter((x) => !toSamo(x, p))].slice(0, MAKS_OSTATNICH);
}

/** × na pigułce usuwa ją z listy. */
export function usunZOstatnich(lista: readonly RecentPlace[], p: RecentPlace): RecentPlace[] {
  return lista.filter((x) => !toSamo(x, p));
}

/** Pełny wpis listy dla wybranego obszaru — karta bierze z niego adres i ikonę. */
export function wpisDlaObszaru(lista: readonly RecentPlace[], a: Pick<RecentPlace, 'label' | 'lat' | 'lon'>): RecentPlace | undefined {
  return lista.find((x) => toSamo(x, a));
}

const RODZAJE: readonly RodzajMiejsca[] = ['school', 'work', 'other'];

/** Wpis z niezaufanego źródła (localStorage, wiersz z bazy) — zły kształt odpada. */
export function poprawnyWpis(v: unknown): RecentPlace | null {
  if (!v || typeof v !== 'object' || Array.isArray(v)) return null;
  const o = v as Record<string, unknown>;
  if (typeof o.label !== 'string' || !o.label.trim()) return null;
  if (typeof o.lat !== 'number' || typeof o.lon !== 'number' || !Number.isFinite(o.lat) || !Number.isFinite(o.lon)) return null;
  const wpis: RecentPlace = { label: o.label, lat: o.lat, lon: o.lon };
  if (typeof o.address === 'string' && o.address) wpis.address = o.address;
  if (typeof o.kind === 'string' && (RODZAJE as readonly string[]).includes(o.kind)) wpis.kind = o.kind as RodzajMiejsca;
  return wpis;
}

export function poprawnaLista(v: unknown): RecentPlace[] {
  if (!Array.isArray(v)) return [];
  return v.map(poprawnyWpis).filter((x): x is RecentPlace => x !== null).slice(0, MAKS_OSTATNICH);
}

/** Klucz w przeglądarce dla niezalogowanego (§1.70). Zalogowany najemca trzyma listę na koncie. */
export const KLUCZ_OSTATNICH = 'fh.blisko.ostatnie';

export function wczytajOstatnie(storage: Pick<Storage, 'getItem'> | null | undefined): RecentPlace[] {
  try {
    const surowe = storage?.getItem(KLUCZ_OSTATNICH);
    return surowe ? poprawnaLista(JSON.parse(surowe)) : [];
  } catch {
    return [];
  }
}

export function zapiszOstatnie(storage: Pick<Storage, 'setItem' | 'removeItem'> | null | undefined, lista: readonly RecentPlace[]): void {
  try {
    if (lista.length) storage?.setItem(KLUCZ_OSTATNICH, JSON.stringify(lista));
    else storage?.removeItem(KLUCZ_OSTATNICH);
  } catch {
    /* tryb prywatny / pełny storage — lista żyje w pamięci komponentu */
  }
}
